<template>
  <LinkCard
    :icon="['fal', 'drafting-compass']"
    :image="thumbnail"
    :badges="badges"
    :is-list="isList"
    :to="$attrs.to || { name: 'project-dashboard', params: { id: value.id } }"
    class="project-link-card"
  >
    <template #title>
      <div class="project-link-card__title">
        <span>{{ $path('name', value) }}</span>

        <img
          v-if="($store.state.enums.UsedProjectLanguages || []).length > 1"
          :alt="i18n['i18n-lang'][value.metadata.language]"
          :src="`/images/flags/${value.metadata.language}.svg`"
          width="15"
        />
      </div>
    </template>
    <template #subtitle>
      <StatusIndicator v-bind="status" />
    </template>
    <template #default>
      <div v-if="addons.length" class="project-link-card__addons">
        <div v-for="addon in addons" :key="addon" v-tooltip.right="$tc('addon:' + addon)">
          <FontAwesomeIcon :icon="addonIcons[addon]" />
        </div>
      </div>
    </template>
  </LinkCard>
</template>

<script>
import LinkCard from '@/components/LinkCard'
import StatusIndicator from '@/components/StatusIndicator'
import { Languages } from '@/components/User/Language'
import { thumbhashToBase64 } from '@/utils/thumbhash'
import getCoverThumbnail from '@/utils/getCoverThumbnail'

export default {
  props: {
    value: Object,
    theme: {
      type: String,
      default: 'default',
    },
    defaultLang: {
      type: String,
      default: 'nb',
    },
  },
  data() {
    return {
      i18n: {
        'i18n-lang': Languages,
      },
    }
  },
  computed: {
    isList() {
      return this.theme === 'list'
    },
    thumbnail() {
      const items = this.$path('value.media.cover') || []
      const src = getCoverThumbnail(items)
      if (!src) return

      const image = items.find(({ url }) => url === src)
      return { src, alt: this.value.name, preview: image ? thumbhashToBase64(image) : null }
    },
    status() {
      if (this.value.isArchived) return { label: this.$t('archived'), status: 'warning' }
      if (this.value.isPublished) return { label: this.$t('published'), status: 'success' }
      return { label: this.$t('unpublished'), status: 'error' }
    },
    badges() {
      if (!this.value.demo) return []
      return [{ value: this.$t('demo'), theme: 'warning' }]
    },
    addons() {
      return (this.$path('value.metadata.addons') || []).filter(a => a in this.addonIcons)
    },

    addonIcons() {
      return {
        'project:presentation': ['fal', 'browser'],
        'project:bid': ['fal', 'sign'],
        'project:purchase': ['fal', 'file-contract'],
        'project:shop': ['fal', 'fill-drip'],
        'project:shop-purchase': ['fal', 'shopping-cart'],
        'project:flatfinder': ['fal', 'layer-plus'],
        'project:complaint': ['fal', 'tasks'],
      }
    },
  },
  components: {
    LinkCard,
    StatusIndicator,
  },
}
</script>

<style lang="scss">
.project-link-card {
  &:hover .project-link-card__addons > * {
    opacity: 1;
    transform: translateX(0);
  }

  &__title {
    display: flex;
    gap: 0.5rem;
    font-weight: 700;
  }

  &__addons {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;

    color: var(--dark);

    & > * {
      background-color: rgba(white, 0.7);
      border: 1px solid rgba(black, 0.05);
      backdrop-filter: blur(8px);
      padding: 5px 8px;
      border-radius: var(--border-radius);

      text-align: center;

      transform: translateX(-10px);
      opacity: 0;

      transition-property: transform, opacity;
      transition-timing-function: ease-out;
      transition-duration: 150ms;
    }
  }
}
</style>
